import type { PendingForm } from '@/api/types/Forms'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { VisitDate, visitTime } from '@/components/VisitDate'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Block } from '@/ui/core/block'
import { Header } from '@/ui/core/header'
import { ExternalLink } from '@/ui/core/link'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Link, router } from 'expo-router'
import { View } from 'react-native'
import { VisitActions } from './VisitActions'

export const LOCATION_ID_TO_MAP: Record<number, string> = {
	1: 'https://maps.app.goo.gl/bYCe4Du9mE8NajMR9',
	2: 'https://maps.app.goo.gl/ytHvPwTwywbuMg3RA',
	3: 'https://maps.app.goo.gl/conGEfYsnRrgamVo7',
}

export const visitTitle = (visit: UpcomingVisit) => visit.types[0]?.name ?? 'Your visit'

const openVisit = (visit: UpcomingVisit) =>
	router.push({ pathname: '/visits/[id]', params: { id: String(visit.appointmentId) } })

/**
 * The next visit is the black section of Home: the date as a white display numeral, the visit
 * beside it, the actions beneath. Black because it is the one thing on the screen that is a
 * commitment rather than information.
 */
export const VisitCard = ({
	visit,
	pendingForms,
	moreCount,
}: { visit: UpcomingVisit; pendingForms: PendingForm[]; moreCount: number }) => (
	<Block tone="ink">
		<VStack gap={20}>
			<HStack className="items-center justify-between">
				<HStack gap={8} className="items-center">
					<Glyph name="tooth" size={18} color={colors.white} />
					<Header variant="h7" tone="white">
						Upcoming visit
					</Header>
				</HStack>
				{moreCount > 0 && (
					<Link href="/care">
						<Text size="body2" tone="white" underlined>
							+{moreCount} more
						</Text>
					</Link>
				)}
			</HStack>

			<Pressable onPress={() => openVisit(visit)} accessibilityRole="button">
				<HStack gap={20} className="items-end">
					<VisitDate startTimeLocal={visit.startTimeLocal} size="lg" tone="white" />
					<VStack gap={6} className="flex-1 pb-2">
						<Header variant="h7" tone="white" className="opacity-70">
							{visitTitle(visit)}
						</Header>
						<Header variant="h3" tone="white">
							{visitTime(visit.startTimeLocal)}
						</Header>
						<ExternalLink
							href={LOCATION_ID_TO_MAP[visit.locationId] ?? LOCATION_ID_TO_MAP[1]}
							className="text-body2 text-white"
						>
							{visit.address}
						</ExternalLink>
					</VStack>
					<Glyph name="chevron" size={16} color={colors.white} />
				</HStack>
			</Pressable>

			{pendingForms.length > 0 && (
				<Pressable
					onPress={() => openVisit(visit)}
					accessibilityRole="button"
					className="rounded-brand bg-mint px-4 py-3 flex-row items-center justify-between"
				>
					<Header variant="h7">
						{pendingForms.length} {pendingForms.length === 1 ? 'form' : 'forms'} to finish
						before you come in
					</Header>
					<Glyph name="arrow" size={14} color={colors.ink} />
				</Pressable>
			)}

			<View className="h-px w-full bg-white opacity-20" />
			<VisitActions visit={visit} onInk />
		</VStack>
	</Block>
)
